import { useFormik } from 'formik';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { MetaRuleGq } from '../../../../../../graphql/projects/models/MetaRuleGq';
import { ProjectGq } from '../../../../../../graphql/projects/models/ProjectGq';
import { UrlMatchTypeGq } from '../../../../../../graphql/projects/models/UrlMatchTypeGq';
import { projectsApiService } from '../../../../../../graphql/projects/projectsApiService';
import { MetaRuleFormState } from './MetaRuleFormState';

export function useMetaRuleForm(
  project: ProjectGq,
  metaRuleId: number,
  setMetaRuleId: Dispatch<SetStateAction<number>>,
  reload: () => Promise<any>
) {
  // Query saved values if we have metaRuleId
  const { query, metaRules } = projectsApiService.queries.useGetMetaRules(
    { projectId: project.id, metaRuleId: metaRuleId, page: 0, rowsPerPage: 10 },
    { skip: !metaRuleId }
  );

  const foundMetaRule = metaRules?.find((r) => r.id === metaRuleId);
  const initialValues = useMemo((): MetaRuleFormState => {
    return {
      projectId: project.id,
      id: metaRuleId,
      urlMatchType: foundMetaRule?.urlMatchType ?? UrlMatchTypeGq.CONTAINS,
      url: foundMetaRule?.url ?? '',
      title: foundMetaRule?.title ?? '',
      h1: foundMetaRule?.h1 ?? '',
      meta: foundMetaRule?.meta ?? '',
      active: foundMetaRule?.active ?? false,
    };
  }, [foundMetaRule]);

  // Mutations
  const createMetaRuleMutation = projectsApiService.mutations.contentPlan.useCreateMetaRule();
  const updateMetaRuleMutation = projectsApiService.mutations.contentPlan.useUpdateMetaRule();

  return useFormik<MetaRuleFormState>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (values.id === null) {
        // Create case
        return createMetaRuleMutation({
          projectId: values.projectId,
          url: values.url,
          urlMatchType: values.urlMatchType,
          title: values.title,
          h1: values.h1,
          meta: values.meta,
        })
          .catch((reason: any) => console.error(reason))
          .then((result: MetaRuleGq) => {
            setMetaRuleId(result.id);
            actions.setValues({
              ...result,
            });
          });
      } else {
        // Update case
        return updateMetaRuleMutation({
          projectId: values.projectId,
          metaRuleId: values.id,
          urlMatchType: values.urlMatchType,
          url: values.url,
          title: values.title,
          h1: values.h1,
          meta: values.meta,
          active: values.active,
        })
          .catch((reason: any) => console.error(reason))
          .then((result: MetaRuleGq) => {
            actions.setValues({
              id: result.id,
              projectId: result.projectId,
              urlMatchType: result.urlMatchType,
              url: result.url,
              title: result.title,
              h1: result.h1,
              meta: result.meta,
              active: result.active,
            });
          });
      }
    },
  });
}
